import { useState, useEffect } from "react";
import { useBlockchainEvents } from "@/contexts/BlockchainEventContext";
import { supabase } from "@/integrations/supabase/client";
import { createDataSynchronizer, TransactionStatus } from "@/lib/data-synchronizer";
import { Connection, PublicKey } from "@solana/web3.js";
import { buildBondingCurveTradeTransaction } from "@/lib/solana-transactions";

export interface TransactionWithStatus {
  id: string;
  coinId: string;
  tradeType: "buy" | "sell";
  amount: number;
  price: number;
  txSignature?: string;
  status: "pending" | "confirmed" | "failed";
  error?: string;
  createdAt: Date;
  confirmedAt?: Date;
  retryCount: number;
}

export function useTransactionStatus(wallet?: any, connection?: Connection) {
  const [transactions, setTransactions] = useState<TransactionWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { eventListener } = useBlockchainEvents();
  const [dataSynchronizer] = useState(() => createDataSynchronizer());

  // Load transactions from database
  useEffect(() => {
    const loadTransactions = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from("trades")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(50);

        if (fetchError) {
          throw fetchError;
        }

        const transactionsWithStatus = data.map((trade) => ({
          id: trade.id,
          coinId: trade.coin_id,
          tradeType: trade.trade_type,
          amount: trade.amount,
          price: trade.price,
          txSignature: trade.tx_hash,
          status: trade.status || "pending", // Default to pending for new records
          error: trade.error || undefined,
          createdAt: new Date(trade.created_at * 1000),
          confirmedAt: trade.confirmed_at ? new Date(trade.confirmed_at * 1000) : undefined,
          retryCount: trade.retry_count || 0,
        }));

        setTransactions(transactionsWithStatus);
      } catch (error) {
        console.error("Error loading transactions:", error);
        setError(error instanceof Error ? error.message : "Failed to load transactions");
      } finally {
        setIsLoading(false);
      }
    };

    loadTransactions();

    // Set up real-time subscription for new trades
    const channel = supabase
      .channel("trades-realtime")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "trades",
        },
        (payload) => {
          const newTransaction: TransactionWithStatus = {
            id: payload.new.id,
            coinId: payload.new.coin_id,
            tradeType: payload.new.trade_type,
            amount: payload.new.amount,
            price: payload.new.price,
            txSignature: payload.new.tx_hash,
            status: payload.new.status || "pending",
            error: payload.new.error || undefined,
            createdAt: new Date(payload.new.created_at * 1000),
            confirmedAt: payload.new.confirmed_at ? new Date(payload.new.confirmed_at * 1000) : undefined,
            retryCount: payload.new.retry_count || 0,
          };

          setTransactions((prev) => [newTransaction, ...prev].slice(0, 50));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Monitor pending transactions
  useEffect(() => {
    if (!eventListener) return;

    const monitorPendingTransactions = async () => {
      const pendingTransactions = transactions.filter(
        (tx) => tx.status === "pending" && tx.txSignature
      );

      for (const tx of pendingTransactions) {
        if (tx.txSignature) {
          try {
            // Check transaction status using data synchronizer
            const isConfirmed = await dataSynchronizer.monitorTransaction(tx.txSignature);

            if (isConfirmed) {
              // Update transaction status
              await updateTransactionStatus(tx.id, "confirmed");

              // Update local state
              setTransactions((prev) =>
                prev.map((t) =>
                  t.id === tx.id
                    ? {
                        ...t,
                        status: "confirmed",
                        confirmedAt: new Date(),
                      }
                    : t
                )
              );
            }
          } catch (error) {
            console.error(`Error monitoring transaction ${tx.txSignature}:`, error);
            await updateTransactionStatus(tx.id, "failed", error instanceof Error ? error.message : "Monitoring failed");
            setTransactions((prev) =>
              prev.map((t) =>
                t.id === tx.id
                  ? {
                      ...t,
                      status: "failed",
                      error: error instanceof Error ? error.message : "Monitoring failed",
                    }
                  : t
              )
            );
          }
        }
      }
    };

    // Check pending transactions every 10 seconds
    const interval = setInterval(monitorPendingTransactions, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [transactions, eventListener]);

  // Update transaction status in database
  const updateTransactionStatus = async (
    transactionId: string,
    status: TransactionWithStatus["status"],
    error?: string
  ) => {
    try {
      const updates: any = {
        status,
        error: error || null,
      };

      if (status === "confirmed") {
        updates.confirmed_at = Math.floor(Date.now() / 1000);
      }

      await supabase
        .from("trades")
        .update(updates)
        .eq("id", transactionId);
    } catch (error) {
      console.error(`Error updating transaction status ${transactionId}:`, error);
      throw error;
    }
  };

  // Create new transaction with pending status
  const createPendingTransaction = async (
    coinId: string,
    tradeType: "buy" | "sell",
    amount: number,
    price: number,
    txSignature?: string
  ): Promise<TransactionWithStatus> => {
    try {
      const newTransaction: TransactionWithStatus = {
        id: crypto.randomUUID(),
        coinId,
        tradeType,
        amount,
        price,
        txSignature,
        status: "pending", // New trades start as pending
        createdAt: new Date(),
        retryCount: 0,
      };

      // Add to database
      const { data, error: dbError } = await supabase
        .from("trades")
        .insert({
          id: newTransaction.id,
          coin_id: coinId,
          trade_type: tradeType,
          amount,
          price,
          tx_hash: txSignature,
          status: newTransaction.status,
          created_at: Math.floor(Date.now() / 1000),
        })
        .select()
        .single();

      if (dbError) {
        throw dbError;
      }

      // Add to local state
      setTransactions((prev) => [newTransaction, ...prev].slice(0, 50));

      // If we have a transaction signature, start monitoring it
      if (txSignature) {
        dataSynchronizer.addTransactionToTrack(txSignature);
      }

      return newTransaction;
    } catch (error) {
      console.error("Error creating pending transaction:", error);
      throw error;
    }
  };

  // Retry failed transaction
  const retryTransaction = async (transactionId: string): Promise<boolean> => {
    try {
      // Check if wallet and connection are available
      if (!wallet || !connection) {
        throw new Error("Wallet and connection required for transaction retry");
      }

      // Find the transaction
      const transaction = transactions.find((t) => t.id === transactionId);
      if (!transaction) {
        throw new Error("Transaction not found");
      }

      if (transaction.status !== "failed") {
        throw new Error("Only failed transactions can be retried");
      }

      // Check retry conditions
      const now = Date.now();
      const createdTime = transaction.createdAt.getTime();
      const timeSinceCreation = now - createdTime;
      const isExpired = timeSinceCreation > 5 * 60 * 1000; // 5 minutes expiry
      const isUserRejected = transaction.error?.toLowerCase().includes("user") ||
                            transaction.error?.toLowerCase().includes("cancel") ||
                            transaction.error?.toLowerCase().includes("reject");

      if (isExpired) {
        throw new Error("Transaction has expired and cannot be retried");
      }

      if (isUserRejected) {
        throw new Error("User-rejected transactions cannot be retried");
      }

      if (transaction.retryCount >= 3) {
        throw new Error("Maximum retry attempts exceeded");
      }

      // Fetch full transaction details from database
      const { data: tradeData, error: fetchError } = await supabase
        .from("trades")
        .select("*")
        .eq("id", transactionId)
        .single();

      if (fetchError || !tradeData) {
        throw new Error("Failed to fetch transaction details");
      }

      // Get token mint address
      const { data: coin } = await supabase
        .from("coins")
        .select("mint_address")
        .eq("id", tradeData.coin_id)
        .single();

      const mintAddress = coin?.mint_address;
      if (!mintAddress) {
        throw new Error("Token configuration not found");
      }

      // Update retry count
      const newRetryCount = transaction.retryCount + 1;
      await supabase
        .from("trades")
        .update({ retry_count: newRetryCount })
        .eq("id", transactionId);

      // Update local state
      setTransactions((prev) =>
        prev.map((t) =>
          t.id === transactionId
            ? {
                ...t,
                retryCount: newRetryCount,
                status: "pending",
                error: undefined,
              }
            : t
        )
      );

      // Rebuild transaction with fresh blockhash
      const rebuiltTransaction = await buildBondingCurveTradeTransaction(
        connection,
        wallet,
        {
          coinId: tradeData.coin_id,
          tradeType: tradeData.type as "buy" | "sell",
          amount: tradeData.type === "buy"
            ? BigInt(Math.floor(tradeData.amount_sol * 1e9)) // SOL to lamports
            : BigInt(Math.floor((tradeData.amount_token / 1_000_000) * 1_000_000 * 1_000_000_000)), // Token amount in millions to smallest units
          userWallet: new PublicKey(wallet.publicKey),
          tokenMintAddress: new PublicKey(mintAddress),
          slippageTolerance: 0.5, // Default slippage
        }
      );

      // Re-sign transaction
      const signedTransaction = await wallet.signTransaction(rebuiltTransaction);

      // Serialize and send
      const serializedTransaction = signedTransaction.serialize();
      const newSignature = await connection.sendRawTransaction(serializedTransaction, {
        skipPreflight: false,
        preflightCommitment: "confirmed",
      });

      // Update transaction record with new signature
      await supabase
        .from("trades")
        .update({
          tx_hash: newSignature,
          status: "pending",
          error: null
        })
        .eq("id", transactionId);

      // Update local state
      setTransactions((prev) =>
        prev.map((t) =>
          t.id === transactionId
            ? {
                ...t,
                txSignature: newSignature,
                status: "pending",
                error: undefined,
              }
            : t
        )
      );

      // Start monitoring the new transaction
      dataSynchronizer.addTransactionToTrack(newSignature);

      // Wait for confirmation
      await connection.confirmTransaction(newSignature, "confirmed");

      // Update as confirmed
      await supabase
        .from("trades")
        .update({
          status: "confirmed",
          confirmed_at: Math.floor(Date.now() / 1000)
        })
        .eq("id", transactionId);

      setTransactions((prev) =>
        prev.map((t) =>
          t.id === transactionId
            ? {
                ...t,
                status: "confirmed",
                confirmedAt: new Date(),
              }
            : t
        )
      );

      return true;
    } catch (error) {
      console.error(`Error retrying transaction ${transactionId}:`, error);

      const errorMessage = error instanceof Error ? error.message : "Retry failed";

      // Update database
      await supabase
        .from("trades")
        .update({
          status: "failed",
          error: errorMessage
        })
        .eq("id", transactionId);

      // Update local state
      setTransactions((prev) =>
        prev.map((t) =>
          t.id === transactionId
            ? {
                ...t,
                status: "failed",
                error: errorMessage,
              }
            : t
        )
      );

      return false;
    }
  };

  // Get transaction status
  const getTransactionStatus = (transactionId: string): TransactionWithStatus | undefined => {
    return transactions.find((t) => t.id === transactionId);
  };

  // Get transactions for a specific coin
  const getTransactionsForCoin = (coinId: string): TransactionWithStatus[] => {
    return transactions.filter((t) => t.coinId === coinId);
  };

  return {
    transactions,
    isLoading,
    error,
    createPendingTransaction,
    updateTransactionStatus,
    retryTransaction,
    getTransactionStatus,
    getTransactionsForCoin,
  };
}

// Hook for monitoring a specific transaction
export function useTransactionMonitor(transactionSignature?: string) {
  const [status, setStatus] = useState<TransactionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataSynchronizer] = useState(() => createDataSynchronizer());

  useEffect(() => {
    if (!transactionSignature) {
      setStatus(null);
      return;
    }

    const monitorTransaction = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check transaction status
        const isConfirmed = await dataSynchronizer.monitorTransaction(transactionSignature);

        if (isConfirmed) {
          setStatus({
            signature: transactionSignature,
            status: "confirmed",
            confirmedAt: new Date(),
            retryCount: 0,
            lastUpdated: new Date(),
          });
        } else {
          const transactionStatus = dataSynchronizer.getTransactionStatus(transactionSignature);
          setStatus(transactionStatus || {
            signature: transactionSignature,
            status: "pending",
            retryCount: 0,
            lastUpdated: new Date(),
          });
        }
      } catch (error) {
        console.error(`Error monitoring transaction ${transactionSignature}:`, error);
        setError(error instanceof Error ? error.message : "Failed to monitor transaction");
        setStatus({
          signature: transactionSignature,
          status: "failed",
          error: error instanceof Error ? error.message : "Failed to monitor transaction",
          retryCount: 0,
          lastUpdated: new Date(),
        });
      } finally {
        setIsLoading(false);
      }
    };

    // Check immediately
    monitorTransaction();

    // Set up polling
    const interval = setInterval(monitorTransaction, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [transactionSignature]);

  return { status, isLoading, error };
}

// Standalone function to create pending transaction (for token creation)
export async function createPendingTransaction(
  coinId: string,
  tradeType: "buy" | "sell",
  amount: number,
  price: number,
  txSignature?: string
): Promise<TransactionWithStatus> {
  try {
    const newTransaction: TransactionWithStatus = {
      id: crypto.randomUUID(),
      coinId,
      tradeType,
      amount,
      price,
      txSignature,
      status: "pending",
      createdAt: new Date(),
      retryCount: 0,
    };

    // Add to database
    const { data, error: dbError } = await supabase
      .from("trades")
      .insert({
        id: newTransaction.id,
        coin_id: coinId,
        trade_type: tradeType,
        amount,
        price,
        tx_hash: txSignature,
        status: newTransaction.status,
        created_at: Math.floor(Date.now() / 1000),
      })
      .select()
      .single();

    if (dbError) {
      throw dbError;
    }

    return newTransaction;
  } catch (error) {
    console.error("Error creating pending transaction:", error);
    throw error;
  }
}